(ns app.front.view.pre-consultation-meetings.view
  (:require [app.domain.group.config.model :as config-model]
            [app.domain.party.model :as party-model]
            [app.domain.workspace.model :as workspace-model]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.section :as section]
            [app.front.util :as futil]
            [app.front.view.meeting.meeting-events :as meeting-events]
            [app.front.view.pre-consultation-meetings.subs :as pre-con-subs]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [re-frame.core-instrumented :as rf]
            [schema.core :as s]))

(defn- iso-date-str
  "Formats a JS date as YYYY-MM-DD string for locale-independent grouping."
  [js-date]
  (some->> js-date
           date-util/js-date->goog-date-time
           (.format date-util/calendar-date-format)))

(defn- today-date []
  (-> (js/Date.)
      iso-date-str))


(defn- min-js-date []
  (-> (js/Date.)
      (date-util/add-months-to-date -12)))


(defn- max-js-date []
  (-> (js/Date.)
      (date-util/add-months-to-date 12)))


(defn- clamp
  "Clamp value between min-val and max-val.

  Examples:
  (clamp 13 12 15) => 13
  (clamp 5 12 15) => 12
  (clamp 18 12 15) => 15"
  [val min-val max-val]
  (->> val
       (max min-val)
       (min max-val)))

(defn- clamp-date
  [js-date]
  (clamp js-date (min-js-date) (max-js-date)))


(def ^:private default-state
  {:selected-tab 0
   :selected-ms  (->> (js/Date.) date-util/start-of-day inst-ms)
   :date-range   {:start (min-js-date)
                  :end   (max-js-date)}})


(rf/reg-event-db ::initialize-state
  (fn [db [_ selected-ms]]
    (->> (when selected-ms
           {:selected-tab 1
            :selected-ms  selected-ms})
         (merge default-state)
         (assoc db ::state))))


(rf/reg-event-db ::update-state
  (fn [db _]
    (let [meetings      (entities/find-entities {:entity-db (:app.front.entities/entity-db db)}
                                                {:domain/state :meeting.state/published})
          meeting-dates (->> meetings (map :meeting/start))]
      (update db
              ::state
              #(merge % {:date-range {:start (apply min meeting-dates)
                                      :end   (apply max meeting-dates)}})))))


(rf/reg-event-fx ::handle-success
  (fn [_ [_ status resp]]
    {:dispatch-n [[::entities/merge-entity-db status resp]
                  [::update-state]]}))


(rf/reg-event-fx ::fetch-published-meetings
  (fn [_ _]
    {:comm/command {:command    :meeting/published-meetings
                    :data       {:from (min-js-date)
                                 :to   (max-js-date)}
                    :method     :get
                    :on-success [::handle-success]}}))

(rf/reg-sub ::state :-> ::state)

(rf/reg-sub ::meetings-grouped-by-date
  :<- [::pre-con-subs/meetings]
  (fn [meetings [_]]
    (->> meetings
         (sort-by :meeting/start)
         (group-by #(-> % :meeting/start iso-date-str)))))


(rf/reg-sub ::selected-ms
  :<- [::state]
  :-> :selected-ms)


(rf/reg-sub ::today?
  :<- [::selected-ms]
  :-> #(->> %
            js/Date.
            iso-date-str
            (= (today-date))))


(rf/reg-event-db ::update-selected-date
  (rf/path [::state])
  (fn [state [_ update-js-date-fn]]
    (update state :selected-ms #(-> %
                                    js/Date.
                                    update-js-date-fn
                                    clamp-date
                                    inst-ms))))


(rf/reg-sub ::date-range
  :<- [::state]
  :-> :date-range)


(rf/reg-sub ::date-range-start
  :<- [::date-range]
  (fn [date-range _]
    (let [min-allowed-date (min-js-date)]
      (-> date-range
          (get :start min-allowed-date)
          (max min-allowed-date)))))


(rf/reg-sub ::date-range-end
  :<- [::date-range]
  (fn [date-range _]
    (let [max-allowed-date (max-js-date)]
      (-> date-range
          (get :end max-allowed-date)
          (min max-allowed-date)))))


(rf/reg-event-db ::set-date-range-start
  (rf/path [::state])
  (fn [state [_ date]]
    (assoc-in state [:date-range :start] date)))


(rf/reg-event-db ::set-date-range-end
  (rf/path [::state])
  (fn [state [_ date]]
    (assoc-in state [:date-range :end] date)))


(rf/reg-sub ::meetings-in-range
  :<- [::date-range]
  :<- [::meetings-grouped-by-date]
  (fn [[{:keys [start end]} meetings] [_ start-new end-new]]
    (let [range-start (iso-date-str (or start-new start))
          range-end   (iso-date-str (or end-new end))]
      (->> meetings
           (filter (fn [[date _]]
                     (and (>= date range-start)
                          (<= date range-end))))))))


(rf/reg-sub ::meetings-in-range-count
  :<- [::meetings-in-range]
  :-> #(->> % vals (map count) (reduce +)))


(rf/reg-sub ::selected-tab
  :<- [::state]
  :-> :selected-tab)


(rf/reg-event-db ::set-selected-tab
  (rf/path [::state])
  (fn [state [_ tab]]
    (assoc state :selected-tab tab)))


(rf/reg-sub ::selected-month
  :<- [::selected-ms]
  :-> #(-> % js/Date. .getMonth))


(rf/reg-sub ::selected-year
  :<- [::selected-ms]
  :-> #(-> % js/Date. .getFullYear))


(rf/reg-sub ::selected-month-dates
  :<- [::selected-ms]
  :-> #(->> %
            js/Date.
            date-util/dates-of-month
            (map iso-date-str)
            set))

(rf/reg-sub ::meetings-for-selected-date
  :<- [::meetings-grouped-by-date]
  :<- [::selected-ms]
  (fn [[meetings date-ms] _]
    (->> date-ms
         js/Date.
         iso-date-str
         (get meetings))))

(rf/reg-sub ::meetings-for-selected-month
  :<- [::meetings-grouped-by-date]
  :<- [::selected-month-dates]
  (fn [[meetings dates] _]
    (select-keys meetings dates)))


(defn- today-in-dates? [dates]
  (some? (some #{(today-date)} dates)))


(rf/reg-sub ::this-month?
  :<- [::selected-month-dates]
  :-> today-in-dates?)


(rf/reg-sub ::selected-week
  :<- [::selected-ms]
  :-> #(-> % js/Date. date-util/get-week-number))


(rf/reg-sub ::selected-week-dates
  :<- [::selected-ms]
  :-> #(->> %
            js/Date.
            date-util/dates-of-week
            (map iso-date-str)
            set))


(rf/reg-sub ::this-week?
  :<- [::selected-week-dates]
  :-> today-in-dates?)


(rf/reg-sub ::meetings-for-selected-week
  :<- [::meetings-grouped-by-date]
  :<- [::selected-week-dates]
  (fn [[meetings dates] _]
    (select-keys meetings dates)))


(defn- meeting-field->meeting-cell [{:keys [field-name field-value key]}]
  [:div.my-4.mx-14
   {:key key}
   [:div.bold.text-xs.pb-2 {:key (str key "-name")} field-name]
   [:div.grid.x.text-sm {:key (str key "-value")} (or field-value "-")]])


(defn- meeting->meeting-fields [meeting]
  (let [lang                (<sub [:language])
        meeting-id          (-> meeting :db/id)
        phase               (-> meeting :entity/parent)
        workspace           (-> meeting :entity/parent :entity/parent)
        tr                  (workspace-model/workspace-tr-fn (<sub [:tr]) workspace)
        workspace-id        (-> workspace :workspace/id)
        [_permit-type-name
         category-name
         _work-type-name
         _work-target-name] (->> workspace
                                 workspace-model/default-config-path
                                 (map config-model/localized-name))
        meeting-date        [:<>
                             [:div {:key "start"}
                              (-> meeting :meeting/start ((<sub [::date-util/date-time-fn])) str)
                              "- "]
                             [:div {:key "end"}
                              (-> meeting :meeting/end ((<sub [::date-util/date-time-fn])) str)]]
        applicants          (some->> phase
                                     party-model/phase-parties
                                     (filter party-model/is-applicant?)
                                     (map (fn [element]
                                            [(:db/id element)
                                             (party-model/corporation-and-full-name-or-full-name element lang)]))
                                     (interpose ",")
                                     (partition-all 2)
                                     (map (fn [[[k v] sep]] [:div {:key k} v sep]))
                                     seq)]
    [{:key         (str meeting-id "-topic")
      :field-name  (tr [:meeting/topic])
      :field-value (-> meeting :meeting/topic)}

     {:key         (str meeting-id "-category")
      :field-name  (tr [:group-config.level/category])
      :field-value category-name}

     {:key         (str meeting-id "-applicants")
      :field-name  (tr [:role/applicant])
      :field-value applicants}

     {:key         (str meeting-id "-address")
      :field-name  (tr [:application/address])
      :field-value (-> workspace workspace-model/full-address)}

     {:key         (str meeting-id "-date-and-time")
      :field-name  (tr [:data-import-type/date-time])
      :field-value meeting-date}

     {:key         (str meeting-id "-link-to-workspace")
      :field-name  (tr [:pre-consultation-meetings/link-to-workspace])
      :field-value [:a {:href (str "/workspace/" workspace-id)} workspace-id]}]))


(defn- meeting->meeting-row [meeting]
  [:div.grid.grid-cols-8.bg-gray-200.border-solid.border-l-8.border-blue-600.rounded
   [:div.col-span-7.grid.grid-cols-3.grid-rows-2
    {:key (str (:db/id meeting) "-meeting-fields")}
    (->> meeting
         meeting->meeting-fields
         (map meeting-field->meeting-cell))]
   [:div.content-center
    {:key (str (:db/id meeting) "-meeting-mark-held-button")}
    [ui/modal-confirmation-button
     (let [tr (<sub [:tr])]
       {:id                  (-> (:db/id meeting) str)
        :tr                  tr
        :trigger-button-text (tr [:meeting/mark-as-held])
        :on-yes-click        #(>evt [::meeting-events/mark-as-held (-> meeting :entity/parent :db/id) (:db/id meeting)])})]]])


(defn- no-meetings [tr]
  [:div (tr [:pre-consultation-meetings/no-meetings-scheduled])])


(defn- meetings-list [tr meetings]
  (if (seq meetings)
    ^{:key "meetings-list"}
    [:div.flex.flex-col.gap-1
     (futil/for-frag [meeting meetings]
       [meeting->meeting-row meeting])]
    [no-meetings tr]))


(defn- ->nav-button [key label update-js-date-fn disabled?]
  (cc/button
    {:key      key
     :kind     :tertiary
     :size     :small
     :disabled disabled?
     :on-click #(>evt [::update-selected-date update-js-date-fn])}
    label))


(defn- navigation-buttons [{:keys [label today-nav-disabled? prev-update-js-date-fn next-update-js-date-fn]}]
  (let [tr               (<sub [:tr])
        selected-js-date (-> (<sub [::selected-ms])
                             js/Date.)
        prev-disabled?   (-> selected-js-date
                             prev-update-js-date-fn
                             (date-util/before? (min-js-date)))
        next-disabled?   (-> selected-js-date
                             next-update-js-date-fn
                             (date-util/after? (max-js-date)))]
    [:div.flex.gap-4
     {:key "navigation-buttons"}
     [:div {:key "today-button-row"}
      (cc/button
        {:key      "today-button"
         :kind     :ghost
         :size     :small
         :disabled today-nav-disabled?
         :on-click #(>evt [::update-selected-date (constantly (js/Date.))])}
        label)]
     [:div.space-x-2 {:key "next-prev-buttons"}
      (->nav-button "prev-button"
                    (tr [:previous])
                    prev-update-js-date-fn
                    prev-disabled?)
      (->nav-button "next-button"
                    (tr [:next])
                    next-update-js-date-fn
                    next-disabled?)]]))


(defn- tab-view [{:keys [headline navigation body]}]
  [:div.bg-white.p-5.rounded
   [:div.flex.justify-between.p-2 {:key "head"}
    [:h2.bold {:key "headline"} headline]
    navigation]
   [:div.flex.flex-col {:key "body"} body]])


(defn- day-tab-view []
  (let [tr               (<sub [:tr])
        selected-js-date (-> (<sub [::selected-ms])
                             js/Date.)
        date             ((<sub [::date-util/date-fn]) selected-js-date)
        day-name         (futil/tr-weekday tr (-> selected-js-date .getDay))]
    [tab-view
     {:headline   (str day-name " " date
                       (when (<sub [::today?])
                         (str " (" (tr [:date/today]) ")")))
      :navigation [navigation-buttons {:label                  (tr [:date/today])
                                       :today-nav-disabled?    (<sub [::today?])
                                       :prev-update-js-date-fn #(date-util/add-days-to-date % -1)
                                       :next-update-js-date-fn #(date-util/add-days-to-date % 1)}]
      :body       [meetings-list tr (<sub [::meetings-for-selected-date])]}]))

(defn- week-body [tr meetings-for-week]
  (if (seq meetings-for-week)
    [:<> (->> meetings-for-week
              (into (sorted-map))
              (map (fn [[local-date meetings-for-date]]
                     (let [js-date (->> local-date date-util/parse-local-date)]
                       [:div
                        {:key local-date}
                        [:h5.bold.pt-3
                         {:key "title"}
                         (str (->> js-date
                                   .getDay
                                   (futil/tr-weekday tr))
                              " "
                              "(" ((<sub [::date-util/date-fn]) js-date) ")")]
                        [meetings-list tr meetings-for-date]])))
              doall)]
    [no-meetings tr]))


(defn- week-tab-view []
  (let [tr                (<sub [:tr])
        chosen-week       (<sub [::selected-week])
        meetings-for-week (<sub [::meetings-for-selected-week])]
    [tab-view
     {:headline   (str (tr [:date/week]) " " chosen-week ", " (<sub [::selected-year])
                       (when (<sub [::this-week?])
                         (str " (" (tr [:datepicker/this-week]) ")")))
      :navigation [navigation-buttons {:label                  (tr [:datepicker/this-week])
                                       :today-nav-disabled?    (<sub [::this-week?])
                                       :prev-update-js-date-fn #(date-util/add-days-to-date % -7)
                                       :next-update-js-date-fn #(date-util/add-days-to-date % 7)}]
      :body       [week-body tr meetings-for-week]}]))

(defn- month-body [tr meetings-for-month]
  (if (seq meetings-for-month)
    [:<> (->> meetings-for-month
              (group-by (fn [[local-date _]]
                          (-> local-date
                              date-util/parse-local-date
                              date-util/get-week-number)))
              (into (sorted-map))
              (map (fn [[week-number meetings-for-week-map]]
                     (let [meeting-year (-> meetings-for-week-map
                                            vals
                                            ffirst
                                            :meeting/start
                                            .getFullYear)]
                       [:div
                        {:key (str "week-" week-number)}
                        [:h4.bold.pt-5
                         {:key "title"}
                         (str (tr [:date/week])
                              " "
                              week-number
                              (when-not (= meeting-year
                                           (date-util/current-year))
                                (str " / " meeting-year)))]
                        [week-body tr meetings-for-week-map]])))
              doall)]
    [no-meetings tr]))

(defn- date-range-body [tr meetings]
  (if (seq meetings)
    [:<> (->> meetings
              ;; group by year
              (group-by (fn [[local-date _]]
                          (-> local-date
                              (str/split #"-")
                              first)))
              (into (sorted-map))
              (map (fn [[year meetings-for-year]]
                     [:div
                      {:key (str "year-" year)}
                      [month-body tr meetings-for-year]]))
              doall)]
    [no-meetings tr]))

(defn- month-tab-view []
  (let [tr           (<sub [:tr])
        chosen-month (<sub [::selected-month])]
    [tab-view
     {:headline   (str (futil/tr-month tr chosen-month) " " (<sub [::selected-year])
                       (when (<sub [::this-month?])
                         (str " (" (tr [:datepicker/this-month]) ")")))
      :navigation [navigation-buttons {:label                  (tr [:datepicker/this-month])
                                       :today-nav-disabled?    (<sub [::this-month?])
                                       :prev-update-js-date-fn #(date-util/add-months-to-date % -1)
                                       :next-update-js-date-fn #(date-util/add-months-to-date % 1)}]
      :body       [month-body tr (<sub [::meetings-for-selected-month])]}]))


(defn- date-range-tab-view []
  (let [tr                   (<sub [:tr])
        chosen-js-date-start (<sub [::date-range-start])
        chosen-js-date-end   (<sub [::date-range-end])
        meetings-in-range    (<sub [::meetings-in-range])
        meetings-count       (<sub [::meetings-in-range-count])]
    [tab-view
     {:headline   (str meetings-count
                       " "
                       (str/lower-case (tr [:meeting/meetings])))
      :navigation [:div.flex
                   [ui/date-picker {:value    chosen-js-date-start
                                    :min-date (min-js-date)
                                    :max-date (max-js-date)
                                    :callback (fn [date]
                                                (>evt [::set-date-range-start date])
                                                (when (> date
                                                         chosen-js-date-end)
                                                  (>evt [::set-date-range-end date])))
                                    :tr       tr}]
                   [:span.p-4 " - "]
                   [ui/date-picker {:value       chosen-js-date-end
                                    :callback    #(>evt [::set-date-range-end %])
                                    :min-date    chosen-js-date-start
                                    :max-date    (max-js-date)
                                    :allow-date? #(>= % chosen-js-date-start)
                                    :tr          tr}]]
      :body       [date-range-body tr meetings-in-range]}]))


(defn meetings-view [_]
  (let [tr           (<sub [:tr])
        selected-tab (<sub [::selected-tab])]
    (when selected-tab
      [:div.grid.cols-1.gap-4
       [:h1.bold (tr [:pre-consultation-meetings/pre-consultation-meetings])]
       [section/tabbed-section
        {:selected-tab-index selected-tab
         :on-change          #(>evt [::set-selected-tab %])
         :tabs               [{:label-text (tr [:pre-consultation-meetings/date-range])
                               :content    [date-range-tab-view {:key "range"}]}
                              {:label-text (tr [:date/day])
                               :content    [day-tab-view {:key "day"}]}
                              {:label-text (tr [:date/week])
                               :content    [week-tab-view {:key "week"}]}
                              {:label-text (tr [:date/month])
                               :content    [month-tab-view {:key "month"}]}]}]])))


(defn- breadcrumb [{:keys [tr]}]
  [:view/pre-consultation-meetings {} (tr [:pre-consultation-meetings/pre-consultation-meetings])])


(def view {:name        :view/pre-consultation-meetings
           :view        meetings-view
           :parameters  {:query {(s/optional-key :selected-ms) s/Int}}
           :breadcrumb  {:items [breadcrumb]}
           :controllers [{:parameters {:query [:selected-ms]}
                          :start      (fn [{{:keys [selected-ms]} :query}]
                                        (>evt [::initialize-state selected-ms])
                                        (>evt [::fetch-published-meetings]))}]})
